/**
 * O que faz: painel técnico da organização, só para admin: modelos, privacidade da rota, tetos diários, cortes,
 *            uso do dia separado por função, fila e calibração por coluna (aprovar/revogar movimento automático) e
 *            a reversão da clínica ao motor legado.
 * Onde usa: SecaoAgentes quando um admin abre a aba Agentes de uma clínica (modoAdmin).
 * Conexões: useResumoOrganizacaoAdmin/useAcoesOrganizacaoAdmin (RPCs admin com auditoria), GavetaSuave, AlertDialog.
 * Tabelas: indiretamente lead_classification_settings, column_rules, route_reviews, daily_budgets, queue,
 *          ai_usage_events e admin_audit — apenas agregados, nenhum texto de paciente.
 * Data: 21/10/2026.
 */
import { useEffect, useId, useState } from 'react';
import { ChevronDown, Loader2, ShieldCheck, SlidersHorizontal } from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { GavetaSuave } from '@/components/ui/gaveta-suave';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import type { CortesOrganizacao } from './contratos';
import type { RegraResumoAdmin } from './servico';
import { useAcoesOrganizacaoAdmin, useResumoOrganizacaoAdmin } from './useOrganizacaoKanban';

const CHAVES_CORTES: Array<{ chave: keyof CortesOrganizacao; rotulo: string }> = [
  { chave: 'sugestao', rotulo: 'Sugestão' },
  { chave: 'automatico', rotulo: 'Automático' },
  { chave: 'margem', rotulo: 'Margem entre opções' },
  { chave: 'evidencia', rotulo: 'Evidência' },
  { chave: 'retorno', rotulo: 'Retorno' },
];
const FUNCOES = [
  { chave: 'lead-classification', rotulo: 'Legado (prompt global)' },
  { chave: 'lead-classification-fast', rotulo: 'Jev (volume)' },
  { chave: 'lead-classification-fallback', rotulo: 'LLM (exceções)' },
];

/** Validação de tela dos tetos (o servidor revalida): inteiros nas faixas e dólares até 100. */
export function validarLimites(campos: { jev: string; llm: string; usd: string }): string | null {
  const jev = Number(campos.jev);
  const llm = Number(campos.llm);
  const usd = Number(campos.usd.replace(',', '.'));
  if (!Number.isInteger(jev) || jev < 0 || jev > 100000) return 'Jev: use um número inteiro de 0 a 100.000.';
  if (!Number.isInteger(llm) || llm < 0 || llm > 1000) return 'LLM: use um número inteiro de 0 a 1.000.';
  if (campos.usd.trim() === '' || Number.isNaN(usd) || usd < 0 || usd > 100) return 'Orçamento: use um valor de US$ 0 a 100.';
  return null;
}

/** Validação de tela dos cortes: números de 0 a 1 e automático nunca abaixo da sugestão. */
export function validarCortes(campos: Record<keyof CortesOrganizacao, string>): string | null {
  for (const { chave, rotulo } of CHAVES_CORTES) {
    const valor = Number(campos[chave].replace(',', '.'));
    if (campos[chave].trim() === '' || Number.isNaN(valor) || valor < 0 || valor > 1) return `${rotulo}: use um número de 0 a 1.`;
  }
  if (Number(campos.automatico.replace(',', '.')) < Number(campos.sugestao.replace(',', '.'))) return 'O corte automático não pode ser menor que o de sugestão.';
  return null;
}

const formatarUsd = (valor: number | null) => (valor == null ? 'desconhecido' : `US$ ${valor.toLocaleString('pt-BR', { maximumFractionDigits: 6 })}`);
const formatarData = (iso: string | null) => (iso
  ? new Intl.DateTimeFormat('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }).format(new Date(iso))
  : '—');

export function LimitesOrganizacaoAdmin({ clinicaId }: { clinicaId: string }) {
  const idPainel = useId();
  const [aberto, setAberto] = useState(false);
  // Consulta só com o painel aberto: admin que não precisa dos detalhes não gera leitura extra.
  const resumo = useResumoOrganizacaoAdmin(clinicaId, aberto);
  const acoes = useAcoesOrganizacaoAdmin(clinicaId);
  const dados = resumo.data;
  const [limites, setLimites] = useState({ jev: '500', llm: '20', usd: '1' });
  const [cortes, setCortes] = useState<Record<keyof CortesOrganizacao, string>>({ sugestao: '0.8', automatico: '0.95', margem: '0.2', evidencia: '0.8', retorno: '0.9' });
  const [referencia, setReferencia] = useState('');
  const [aprovando, setAprovando] = useState<RegraResumoAdmin | null>(null);
  const [confirmarDesativar, setConfirmarDesativar] = useState(false);

  // Formulários partem dos valores do servidor sempre que o resumo é recarregado.
  useEffect(() => {
    if (!dados) return;
    setLimites({ jev: String(dados.limites.jev), llm: String(dados.limites.llm), usd: String(dados.limites.usd) });
    setCortes(Object.fromEntries(CHAVES_CORTES.map(({ chave }) => [chave, String(dados.cortes[chave])])) as Record<keyof CortesOrganizacao, string>);
    setReferencia(dados.privacidade.referencia ?? '');
  }, [dados]);

  const erroLimites = validarLimites(limites);
  const erroCortes = validarCortes(cortes);

  return (
    <Card className="lg-card">
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3 space-y-0">
        <div className="space-y-1">
          <CardTitle className="flex items-center gap-2 text-base"><SlidersHorizontal className="h-4 w-4" aria-hidden="true" />Organização — painel técnico</CardTitle>
          <CardDescription>Visível só para administradores. A clínica escolhe apenas frequência e se permite mover.</CardDescription>
        </div>
        <Button variant="outline" className="rounded-full" aria-expanded={aberto} aria-controls={idPainel} onClick={() => setAberto((valor) => !valor)}>
          {aberto ? 'Ocultar detalhes' : 'Mostrar detalhes'}
          <ChevronDown className={cn('ml-1 h-4 w-4 transition-transform duration-200 motion-reduce:transition-none', aberto && 'rotate-180')} aria-hidden="true" />
        </Button>
      </CardHeader>
      <GavetaSuave aberta={aberto} id={idPainel}>
        <CardContent className="space-y-6">
          {resumo.isLoading && <p role="status" className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />Carregando painel…</p>}
          {resumo.isError && (
            <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
              Não foi possível carregar o painel técnico.
              <Button size="sm" variant="outline" onClick={() => void resumo.refetch()}>Tentar novamente</Button>
            </div>
          )}
          {dados && (
            <>
              <section className="space-y-2" aria-labelledby={`${idPainel}-modelos`}>
                <h4 id={`${idPainel}-modelos`} className="text-sm font-semibold">Modelos e privacidade</h4>
                <dl className="grid gap-1 text-xs sm:grid-cols-3">
                  <div><dt className="text-muted-foreground">Jev</dt><dd className="break-all">{dados.modelos.jev ?? 'não configurado'}</dd></div>
                  <div><dt className="text-muted-foreground">LLM de exceção</dt><dd className="break-all">{dados.modelos.llm ?? 'não configurado'}</dd></div>
                  <div><dt className="text-muted-foreground">Legado</dt><dd className="break-all">{dados.modelos.legado ?? 'não configurado'}</dd></div>
                </dl>
                <p className="text-xs text-muted-foreground">Os modelos mudam em Modelos de IA. Trocar Jev ou LLM remove a validação de privacidade e as liberações automáticas.</p>
                <div className="space-y-2 rounded-xl border border-border p-3">
                  <p className="flex flex-wrap items-center gap-2 text-sm">
                    <ShieldCheck className="h-4 w-4" aria-hidden="true" />
                    <span className={cn('lg-selo', dados.privacidade.validada ? 'lg-selo--sucesso' : 'lg-selo--neutro')}>
                      {dados.privacidade.validada ? `Validada em ${formatarData(dados.privacidade.validadaEm)}` : 'Privacidade não validada'}
                    </span>
                  </p>
                  {!dados.privacidade.validada && (
                    <p className="text-xs text-muted-foreground">Sem validação, conversas reais não vão para Jev nem LLM; ficam só os registros do CRM e a revisão.</p>
                  )}
                  <Label htmlFor={`${idPainel}-referencia`}>Referência da política verificada</Label>
                  <Input id={`${idPainel}-referencia`} value={referencia} maxLength={300} placeholder="Ex.: termos de retenção revisados em 21/10/2026"
                    onChange={(evento) => setReferencia(evento.target.value)} />
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" className="rounded-full" disabled={!referencia.trim() || acoes.registrarPrivacidade.isPending}
                      onClick={() => acoes.registrarPrivacidade.mutate({ validada: true, referencia })}>Registrar validação</Button>
                    {dados.privacidade.validada && (
                      <Button size="sm" variant="outline" className="rounded-full" disabled={acoes.registrarPrivacidade.isPending}
                        onClick={() => acoes.registrarPrivacidade.mutate({ validada: false, referencia })}>Remover validação</Button>
                    )}
                  </div>
                </div>
              </section>

              <section className="space-y-2" aria-labelledby={`${idPainel}-limites`}>
                <h4 id={`${idPainel}-limites`} className="text-sm font-semibold">Tetos diários (horário de São Paulo)</h4>
                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="space-y-1"><Label htmlFor={`${idPainel}-jev`}>Chamadas Jev</Label>
                    <Input id={`${idPainel}-jev`} inputMode="numeric" value={limites.jev} onChange={(evento) => setLimites((atual) => ({ ...atual, jev: evento.target.value }))} /></div>
                  <div className="space-y-1"><Label htmlFor={`${idPainel}-llm`}>Chamadas LLM</Label>
                    <Input id={`${idPainel}-llm`} inputMode="numeric" value={limites.llm} onChange={(evento) => setLimites((atual) => ({ ...atual, llm: evento.target.value }))} /></div>
                  <div className="space-y-1"><Label htmlFor={`${idPainel}-usd`}>Orçamento (US$)</Label>
                    <Input id={`${idPainel}-usd`} inputMode="decimal" value={limites.usd} onChange={(evento) => setLimites((atual) => ({ ...atual, usd: evento.target.value }))} /></div>
                </div>
                <p className="text-xs text-muted-foreground">Zero suspende aquela finalidade. Usado hoje: Jev {dados.usoHoje.cotas.jev}/{dados.limites.jev} · LLM {dados.usoHoje.cotas.llm}/{dados.limites.llm} · custo {formatarUsd(dados.usoHoje.custoUsd)}.</p>
                {erroLimites && <p role="alert" className="text-xs lg-texto-perigo">{erroLimites}</p>}
                <Button size="sm" className="rounded-full" disabled={Boolean(erroLimites) || acoes.definirLimites.isPending}
                  onClick={() => acoes.definirLimites.mutate({ jev: Number(limites.jev), llm: Number(limites.llm), usd: Number(limites.usd.replace(',', '.')) })}>Salvar tetos</Button>
              </section>

              <section className="space-y-2" aria-labelledby={`${idPainel}-cortes`}>
                <h4 id={`${idPainel}-cortes`} className="text-sm font-semibold">Cortes técnicos</h4>
                <div className="grid gap-3 sm:grid-cols-5">
                  {CHAVES_CORTES.map(({ chave, rotulo }) => (
                    <div key={chave} className="space-y-1"><Label htmlFor={`${idPainel}-corte-${chave}`} className="text-xs">{rotulo}</Label>
                      <Input id={`${idPainel}-corte-${chave}`} inputMode="decimal" value={cortes[chave]} onChange={(evento) => setCortes((atual) => ({ ...atual, [chave]: evento.target.value }))} /></div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">Não são garantia de acerto. Salvar novos cortes revoga as liberações automáticas; não reanalisa a base.</p>
                {erroCortes && <p role="alert" className="text-xs lg-texto-perigo">{erroCortes}</p>}
                <Button size="sm" variant="outline" className="rounded-full" disabled={Boolean(erroCortes) || acoes.definirCortes.isPending}
                  onClick={() => acoes.definirCortes.mutate(Object.fromEntries(CHAVES_CORTES.map(({ chave }) => [chave, Number(cortes[chave].replace(',', '.'))])) as unknown as CortesOrganizacao)}>
                  Salvar cortes
                </Button>
              </section>

              <section className="space-y-2" aria-labelledby={`${idPainel}-uso`}>
                <h4 id={`${idPainel}-uso`} className="text-sm font-semibold">Uso de hoje e fila</h4>
                <ul className="grid gap-2 text-xs sm:grid-cols-3">
                  {FUNCOES.map(({ chave, rotulo }) => (
                    <li key={chave} className="rounded-lg border border-border p-2">
                      <p className="text-muted-foreground">{rotulo}</p>
                      <p>{dados.usoHoje.porFuncao[chave]?.chamadas ?? 0} chamadas · {formatarUsd(dados.usoHoje.porFuncao[chave] ? dados.usoHoje.porFuncao[chave].custoUsd : 0)}</p>
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-muted-foreground">
                  Fila: {dados.fila.pendentes} pendentes · mais antiga vencida {formatarData(dados.fila.vencidaMaisAntiga)} ·
                  {' '}{dados.fila.emProcessamento} em processamento · {dados.fila.comFalha} com falha · próxima janela {formatarData(dados.proximaJanela)}
                </p>
              </section>

              <section className="space-y-2" aria-labelledby={`${idPainel}-calibracao`}>
                <h4 id={`${idPainel}-calibracao`} className="text-sm font-semibold">Calibração por coluna</h4>
                <p className="text-xs text-muted-foreground">Amostras vêm de “Aceitar”, “Manter” e “Corrigir” na revisão, só da versão atual da regra e dos modelos. Liberar exige 100 amostras, 98% de precisão e nenhum falso evento.</p>
                {dados.regras.length === 0 ? (
                  <p className="text-sm text-muted-foreground">A clínica ainda não publicou regras de colunas.</p>
                ) : (
                  <ul className="divide-y divide-border rounded-xl border border-border">
                    {dados.regras.map((regra) => {
                      const calibracao = regra.calibracao;
                      return (
                        <li key={regra.etapaId} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
                          <div className="min-w-0 space-y-0.5">
                            <p className="text-sm font-medium">{regra.nome} <span className="text-xs font-normal text-muted-foreground">· versão {regra.versao}</span></p>
                            <p className="text-xs text-muted-foreground">
                              {calibracao ? `${calibracao.amostras} amostras · precisão ${calibracao.precisao == null ? '—' : `${(calibracao.precisao * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`} · ${calibracao.falsosEventos} falsos eventos` : 'Sem calibração'}
                              {regra.modo === 'automatic' ? ' · clínica escolheu mover' : ''}
                            </p>
                          </div>
                          {regra.aprovadaVigente ? (
                            <Button size="sm" variant="outline" className="rounded-full" disabled={acoes.revogarRegra.isPending} onClick={() => acoes.revogarRegra.mutate(regra.etapaId)}>Revogar liberação</Button>
                          ) : (
                            <Button size="sm" className="rounded-full" disabled={!calibracao?.elegivel || acoes.aprovarRegra.isPending} onClick={() => setAprovando(regra)}>
                              {calibracao?.elegivel ? 'Liberar movimento' : 'Ainda não calibrada'}
                            </Button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>

              {/* Reversão por clínica (manual de operação): volta ao legado sem apagar regras nem histórico. */}
              {dados.motor === 'hybrid' && (
                <section className="space-y-2 rounded-xl border border-destructive/40 p-3" aria-labelledby={`${idPainel}-reversao`}>
                  <h4 id={`${idPainel}-reversao`} className="text-sm font-semibold">Voltar ao motor legado</h4>
                  <p className="text-xs text-muted-foreground">
                    Para a organização personalizada desta clínica: revoga movimentos automáticos e tira as sugestões da revisão.
                    Regras, correções, calibração e histórico ficam guardados; a clínica pode reativar pelo assistente.
                  </p>
                  <Button size="sm" variant="outline" className="rounded-full" disabled={acoes.desativar.isPending} onClick={() => setConfirmarDesativar(true)}>
                    Voltar esta clínica ao motor legado
                  </Button>
                </section>
              )}
            </>
          )}
        </CardContent>
      </GavetaSuave>

      <AlertDialog open={Boolean(aprovando)} onOpenChange={(valor) => { if (!valor) setAprovando(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Liberar movimento automático?</AlertDialogTitle>
            <AlertDialogDescription>
              A coluna <strong>{aprovando?.nome}</strong> poderá mover cards sozinha, se a clínica escolher, enquanto a regra (versão {aprovando?.versao}) e os modelos forem os mesmos.
              Colunas com evento Meta continuam exigindo a autorização da clínica.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => { if (aprovando) acoes.aprovarRegra.mutate({ etapaId: aprovando.etapaId, versao: aprovando.versao }); setAprovando(null); }}>
              Liberar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmarDesativar} onOpenChange={setConfirmarDesativar}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Voltar esta clínica ao motor legado?</AlertDialogTitle>
            <AlertDialogDescription>
              A organização por colunas para de analisar esta clínica e as sugestões dela saem da revisão. Nada é apagado:
              regras, correções, calibração e histórico continuam guardados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => { acoes.desativar.mutate(); setConfirmarDesativar(false); }}>
              Voltar ao legado
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
